
import React from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useUIStore, CommandItem as CommandItemType } from '@/store/uiStore';
import { useSessionStore } from '@/store/sessionStore';
import { useContextMenuTemplateStore } from '@/store/contextTemplateStore';
import { useSystemPromptStore } from '@/store/systemPromptStore';
import { useCustomSnippetStore } from '@/store/customSnippetStore';
import { Book, Code, FileCode, History, Home, Pencil, Play, Settings, PlusCircle, RotateCcw, DraftingCompass, Terminal, FileText, Network } from 'lucide-react';
import { ContextTemplate, SystemPrompt, CustomSnippet, Session, ContextSnippet } from '@/types/api';
import { useContextSnippetStore } from '@/store/contextSnippetStore';

export default function CommandPalette() {
  const { isCommandPaletteOpen, closeCommandPalette, recentCommands, addRecentCommand } = useUIStore();
  const { setActiveMainPage, setCurrentSessionId } = useSessionStore();
  const { setEditingTemplateId } = useContextMenuTemplateStore();
  const { setEditingPromptId } = useSystemPromptStore();
  const { setEditingSnippetId } = useCustomSnippetStore();
  const { setEditingSnippetId: setEditingContextSnippetId } = useContextSnippetStore();

  const runCommand = (command: CommandItemType) => {
    addRecentCommand(command);
    command.action();
    closeCommandPalette();
  };

  const navigationCommands: CommandItemType[] = [
    { id: 'nav-home', value: 'Navigate to Home dashboard', label: 'Home', action: () => setActiveMainPage('home'), icon: <Home className="mr-2 h-4 w-4" /> },
    { id: 'nav-plan', value: 'Navigate to Plan Interaction', label: 'Plan Interaction', action: () => setActiveMainPage('plan'), icon: <Play className="mr-2 h-4 w-4" /> },
    { id: 'nav-markdown-editor', value: 'Navigate to Markdown Editor', label: 'Markdown Editor', action: () => setActiveMainPage('markdown_editor'), icon: <FileText className="mr-2 h-4 w-4" /> },
    { id: 'nav-context', value: 'Navigate to Context Templates', label: 'Context Templates', action: () => setActiveMainPage('context'), icon: <FileCode className="mr-2 h-4 w-4" /> },
    { id: 'nav-prompts', value: 'Navigate to System Prompts', label: 'System Prompts', action: () => setActiveMainPage('system_prompts'), icon: <Terminal className="mr-2 h-4 w-4" /> },
    { id: 'nav-snippets', value: 'Navigate to Template Snippets', label: 'Template Snippets', action: () => setActiveMainPage('custom_snippets'), icon: <Code className="mr-2 h-4 w-4" /> },
    { id: 'nav-context-snippets', value: 'Navigate to Prompt Snippets', label: 'Prompt Snippets', action: () => setActiveMainPage('context_snippets'), icon: <DraftingCompass className="mr-2 h-4 w-4" /> },
    { id: 'nav-history', value: 'Navigate to Session History sess', label: 'Session History', action: () => setActiveMainPage('history'), icon: <History className="mr-2 h-4 w-4" /> },
    { id: 'nav-mcp', value: 'Navigate to MCP Management', label: 'MCP Management', action: () => setActiveMainPage('mcp'), icon: <Network className="mr-2 h-4 w-4" /> },
    { id: 'nav-settings', value: 'Navigate to Settings', label: 'Settings', action: () => setActiveMainPage('settings'), icon: <Settings className="mr-2 h-4 w-4" /> },
  ];

  const templates = useContextMenuTemplateStore(state => state.templates);
  const prompts = useSystemPromptStore(state => state.systemPrompts);
  const snippets = useCustomSnippetStore(state => state.snippets);
  const contextSnippets = useContextSnippetStore(state => state.snippets);
  const sessions = useSessionStore(state => state.sessions);


  const templateCommands: CommandItemType[] = [
    {
      id: 'create-template',
      value: 'Create New Context Template',
      label: 'Create New Context Template',
      action: () => { setActiveMainPage('context'); setEditingTemplateId('new'); },
      icon: <PlusCircle className="mr-2 h-4 w-4" />
    },
    ...templates.map((template: ContextTemplate): CommandItemType => ({
      id: `edit-template-${template.id}`,
      value: `Edit Context Template: ${template.template_name}`,
      label: `Edit: ${template.template_name}`,
      action: () => { setActiveMainPage('context'); setEditingTemplateId(template.id); },
      icon: <Pencil className="mr-2 h-4 w-4" />
    }))
  ];

  const promptCommands: CommandItemType[] = [
    {
      id: 'create-prompt',
      value: 'Create New System Prompt',
      label: 'Create New System Prompt',
      action: () => { setActiveMainPage('system_prompts'); setEditingPromptId('new'); },
      icon: <PlusCircle className="mr-2 h-4 w-4" />
    },
    ...prompts.map((prompt: SystemPrompt): CommandItemType => ({
      id: `edit-prompt-${prompt.id}`,
      value: `Edit System Prompt: ${prompt.prompt_name}`,
      label: `Edit: ${prompt.prompt_name}`,
      action: () => { setActiveMainPage('system_prompts'); setEditingPromptId(prompt.id); },
      icon: <Pencil className="mr-2 h-4 w-4" />
    }))
  ];

  const snippetCommands: CommandItemType[] = [
     {
      id: 'create-snippet',
      value: 'Create New Template Snippet',
      label: 'Create New Template Snippet',
      action: () => { setActiveMainPage('custom_snippets'); setEditingSnippetId('new'); },
      icon: <PlusCircle className="mr-2 h-4 w-4" />
    },
    ...snippets.map((snippet: CustomSnippet): CommandItemType => ({
      id: `edit-snippet-${snippet.id}`,
      value: `Edit Template Snippet: ${snippet.prefix}`,
      label: `Edit: ${snippet.prefix}`,
      action: () => { setActiveMainPage('custom_snippets'); setEditingSnippetId(snippet.id); },
      icon: <Pencil className="mr-2 h-4 w-4" />
    }))
  ];

  const contextSnippetCommands: CommandItemType[] = [
    {
     id: 'create-context-snippet',
     value: 'Create New Prompt Snippet',
     label: 'Create New Prompt Snippet',
     action: () => { setActiveMainPage('context_snippets'); setEditingContextSnippetId('new'); },
     icon: <PlusCircle className="mr-2 h-4 w-4" />
   },
   ...contextSnippets.map((snippet: ContextSnippet): CommandItemType => ({
     id: `edit-context-snippet-${snippet.id}`,
     value: `Edit Prompt Snippet: ${snippet.handle}`,
     label: `Edit: !${snippet.handle}`,
     action: () => { setActiveMainPage('context_snippets'); setEditingContextSnippetId(snippet.id); },
     icon: <Pencil className="mr-2 h-4 w-4" />
   }))
 ];

  const sessionCommands: CommandItemType[] = sessions.map((session: Session): CommandItemType => ({
    id: `load-session-${session.id}`,
    value: `Load Session: ${session.session_title || 'Untitled Session'}`,
    label: `Load: ${session.session_title || 'Untitled Session'}`,
    action: () => { setCurrentSessionId(session.id); setActiveMainPage('plan'); },
    icon: <Book className="mr-2 h-4 w-4" />
  }));

  const renderCommandGroup = (heading: string, commands: CommandItemType[]) => (
    <CommandGroup heading={heading}>
      {commands.map((command) => (
        <CommandItem
          key={command.id}
          value={command.value}
          onSelect={() => runCommand(command)}
        >
          {command.icon}
          <span>{command.label}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );

  return (
    <CommandDialog open={isCommandPaletteOpen} onOpenChange={(open) => !open && closeCommandPalette()} className="md:max-w-3xl">
      <CommandInput placeholder="Type a command or search..." />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>No results found.</CommandEmpty>

        {recentCommands.length > 0 && (
          <CommandGroup heading="Recent">
            {recentCommands.map((command) => (
              <CommandItem
                key={command.id}
                value={command.value}
                onSelect={() => runCommand(command)}
              >
                {command.icon || <RotateCcw className="mr-2 h-4 w-4" />}
                <span>{command.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {renderCommandGroup("Navigation", navigationCommands)}
        {renderCommandGroup("Context Templates", templateCommands)}
        {renderCommandGroup("System Prompts", promptCommands)}
        {renderCommandGroup("Template Snippets", snippetCommands)}
        {renderCommandGroup("Prompt Snippets", contextSnippetCommands)}
        {renderCommandGroup("Sessions", sessionCommands)}

      </CommandList>
    </CommandDialog>
  );
}